import { Capacitor } from '@capacitor/core'
import { EvergreenWealthLogo } from '@egw/assets'
import { useAuth } from '@egw/auth'
import { Link } from '@egw/components'
import { type FeatureFlags, WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import cn from 'classnames'
import { useState, type FC, type PropsWithChildren, useMemo } from 'react'
import { useLocation } from 'react-router'
import { Chat } from '@egw/features-chat'
import { useFlags } from '@egw/feature-flags'
import { isCopilotKitRuntimeAvailable } from '@egw/ai-chat'
import { MobileNavigation } from '../mobile'
import { VerticalNavigation } from '../vertical-navigation'

export const RedesignLayout: FC<PropsWithChildren> = ({ children }) => {
  const { pathname } = useLocation()
  const { isLoading, isAuthenticated } = useAuth()
  const [isChatOpen, setIsChatOpen] = useState(false)

  const { t } = useTranslation('layouts')

  const { flags } = useFlags<FeatureFlags>()

  const { isLoading: __, isAuthenticated: ___ } = useAuth()
  const currentRoute = Object.values(WEALTH_ROUTES).find((route) => {
    if (route.path === pathname) {
      return route.isShowAIChat
    }
  })

  const hideNavigation = pathname.includes('/recovery')

  // Check if CopilotKit is available before rendering Chat component
  const isCopilotKitEnabled = useMemo(() => isCopilotKitRuntimeAvailable(), [])

  return (
    <div className="full-viewport-height w-full flex flex-col">
      {!isLoading && (!isAuthenticated || hideNavigation) ? (
        <div
          className={cn('px-sm py-sm bg-black items-center gap-sm flex', {
            'pt-14': Capacitor.isNativePlatform(),
            'sm:h-3xl': !flags.rolloutSignInPageRedesign,
          })}
        >
          <div className="flex-1">
            <Link href={WEALTH_ROUTES.OVERVIEW.path} title={t('header.home_link_title')} data-testid="layout-home-link">
              <EvergreenWealthLogo className="h-3" />
            </Link>
          </div>
        </div>
      ) : null}

      {/* Mobile Navigation Header */}
      {!isLoading && isAuthenticated && !hideNavigation ? (
        <MobileNavigation isChatOpen={isChatOpen} setIsChatOpen={setIsChatOpen} />
      ) : null}

      <div className="flex-1 flex flex-row min-h-0">
        {/* Desktop Navigation */}
        {!isLoading && isAuthenticated && !hideNavigation ? (
          <div className="hidden lg:flex w-[222px] h-full rounded-r-[24px]">
            <VerticalNavigation setIsChatOpen={setIsChatOpen} className="rounded-r-[24px]" />
          </div>
        ) : null}

        {/* Main Content */}
        <div className="flex flex-col h-full w-full relative overflow-hidden min-h-0 core-ui-redesign">
          <div className="flex flex-col relative flex-1 overflow-auto min-h-0">{children}</div>
          {flags.rolloutCopilotKit && 
           currentRoute?.isShowAIChat && 
           !isLoading && 
           isAuthenticated && 
           isCopilotKitEnabled ? (
            <Chat isChatOpen={isChatOpen} setIsChatOpen={setIsChatOpen} />
          ) : null}
        </div>
      </div>
    </div>
  )
}
